import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileText, Download, ArrowRight } from "lucide-react";

const PETITIONS = [
  {
    n: "01",
    slug: "peoples-fund",
    title: "Amending the People's Fund Plan",
    text: "Requires at least 25% of all natural resource revenue to go into the People's Fund first — held and managed for the benefit of current and future generations.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/ltkia5b8_7%201%202026%20Petition%20Amending%20the%20People%27s%20Fund%20Plan.docx",
  },
  {
    n: "02",
    slug: "petition-expense-reimbursement",
    title: "Reimbursement for Bringing a Successful Petition",
    text: "Tribal members or groups who bring a successful petition for a referendum or Secretarial election get their reasonable, documented expenses reimbursed.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/tjdlqiki_7%201%202026%20A%20Petition%20Authoriziing%20the%20Reimbursement%20of%20Fees%20and%20Expenses%20for%20Bringing%20a%20Successful%20Petition.pdf",
  },
  {
    n: "03",
    slug: "attorney-fees",
    title: "Reimbursement of Attorney Fees for Successful Equitable Relief",
    text: "Members and businesses who win equitable relief against the Tribal Business Council or its agencies are reimbursed for reasonable fees and court costs.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/robunrfu_7%201%202026%20Petition%20Approving%20Reimbursement%20of%20Attorney%20Fees%20for%20Successful%20Equitable%20Relief.docx",
  },
  {
    n: "04",
    slug: "sovereign-immunity",
    title: "Limited Waiver of Sovereign Immunity",
    text: "Lets enrolled members seek court orders that make officials follow tribal law — injunctions and enforcement only, no money damages.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/whxsx8j1_7%201%202026%20Petition%20for%20Limited%20Waiver%20of%20Sovereign%20Immunity.docx",
  },
  {
    n: "05",
    title: "Tolling Statutes of Limitation for Official Misconduct",
    text: "The clock stops on prosecuting crimes by tribal officials or employees while they hold office — and for five years after — so concealed misconduct can still be investigated and prosecuted.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/02izrqz1_7%201%202026%20Petition%20Tolling%20Criminal%20Statutes%20of%20Limitation%20for%20Official%20Misconduct.docx",
  },
  {
    n: "06",
    title: "Payment & Per Diem for Off-Reservation Voters",
    text: "Pays mileage and per diem for enrolled members who live off the reservation and travel home to vote in tribal primary and general elections — including 2026, even if approved afterward.",
    url: "https://customer-assets-wrfwihn1.emergentagent.net/job_williams-nation-vote/artifacts/bwl4mym1_7%201%202026%20A%20Petition%20Approving%20Payment%20and%20Per%20Diem%20for%20All%20Off%20Reservation%20Voters.pdf",
  },
];

export default function Referendums() {
  return (
    <section id="referendum" className="relative bg-navy noise py-24 md:py-36 px-5 md:px-10 overflow-hidden" data-testid="referendum-section">
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-xs uppercase tracking-[0.3em] font-semibold text-gold mb-6"
        >
          By Vote of the People
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#F8F9FA] mb-8"
        >
          Referendum
          <span className="italic font-medium text-white/85"> Solutions</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.18 }}
          className="text-white/90 text-base md:text-lg leading-relaxed max-w-2xl mb-14 md:mb-20"
        >
          Real power, decided by real votes. These six petitions — each a referendum vote to the Three
          Affiliated Tribes — put the biggest decisions directly in the hands of the people.
        </motion.p>

        <div className="divide-y divide-white/10 border-y border-white/10">
          {PETITIONS.map((p, i) => (
            <motion.article
              key={p.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.06 }}
              className="group py-7 md:py-9 px-2 md:px-4 hover:bg-white/[0.03] transition-colors duration-300"
              data-testid={`referendum-row-${p.n}`}
            >
              <div className="flex items-start gap-4">
                <FileText className="w-5 h-5 text-gold mt-1.5 shrink-0" strokeWidth={1.5} />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-gold mb-1.5">
                    Petition {p.n} · For Referendum Vote
                  </p>
                  <h3 className="font-sans text-lg md:text-xl font-bold tracking-tight text-[#F8F9FA] group-hover:text-gold transition-colors duration-300">
                    {p.title}
                  </h3>
                  <p className="text-sm md:text-base text-white/90 mt-2 leading-relaxed max-w-2xl">{p.text}</p>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3">
                    <Link
                      to={`/petition/${p.slug}`}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white hover:text-gold transition-colors duration-300"
                      data-testid={`referendum-read-${p.n}`}
                    >
                      Read the full text <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-white/60 hover:text-gold transition-colors duration-300"
                      data-testid={`referendum-download-${p.n}`}
                    >
                      <Download className="w-3.5 h-3.5" /> Original document
                    </a>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
